import type { CalendarDate } from "@internationalized/date";
import { useBestFaresCalendar } from "../../hooks/use-best-fares-calendar";
import type {
  BestFaresCalendarQueryInput,
  UseBestFaresCalendarInput,
} from "../../hooks/use-best-fares-calendar";
import type { FlightSearchFormLabels } from "../../labels";
import { formatCurrency, formatPoints, formatTax } from "./format-price";
import { getToolbarMessage } from "../../toolbar/toolbar-message";
import {
  getSelectedToolbarPrice,
  type SelectedToolbarPrice,
} from "../../toolbar/toolbar-price";
import { useBestFaresRenderDate } from "./render-date";

type FareDirection = "inbound" | "outbound";

export type BestFaresFocusState = "start" | "end" | "none";

const ANNOUNCEMENT_TRIP_LABELS: Record<
  SelectedToolbarPrice["tripType"],
  string
> = {
  "one-way": "one-way per person",
  "round-trip": "roundtrip per person",
};

type GetToolbarAnnouncementInput = {
  isError: boolean;
  isLoading: boolean;
  isRequestEnabled: boolean;
  selectedPrice?: SelectedToolbarPrice;
};

const getToolbarAnnouncement = ({
  isError,
  isLoading,
  isRequestEnabled,
  selectedPrice,
}: GetToolbarAnnouncementInput): string | undefined => {
  if (!isRequestEnabled || isLoading) {
    return undefined;
  }

  if (isError) {
    return "Pricing unavailable";
  }

  if (!selectedPrice) {
    return "Pricing loaded";
  }

  const { amount, currencyCode, tax, tripType, usePoints } = selectedPrice;
  let total: string;

  if (usePoints) {
    total = `${formatPoints(amount)} points`;

    if (tax) {
      total = `${total} plus ${formatTax(tax, currencyCode)}`;
    }
  } else {
    total = formatCurrency(amount, currencyCode);
  }

  return `Price updated: ${total} ${ANNOUNCEMENT_TRIP_LABELS[tripType]}`;
};

type UseBestFaresPickerStateInput = Pick<
  UseBestFaresCalendarInput,
  | "destination"
  | "endDate"
  | "passengers"
  | "showDatePickerPricing"
  | "startDate"
  | "usePoints"
> &
  BestFaresCalendarQueryInput & {
    fareDirection: FareDirection;
    focusState?: BestFaresFocusState;
    selectedDepartDate?: CalendarDate;
    selectedReturnDate?: CalendarDate;
    toolbarLabels: FlightSearchFormLabels["datePickerToolbar"];
    tripType: Extract<
      UseBestFaresCalendarInput["tripType"],
      "one-way" | "round-trip"
    >;
  };

export const useBestFaresPickerState = ({
  destination,
  endDate,
  fareDirection,
  focusState,
  origin,
  passengers,
  selectedDepartDate,
  selectedReturnDate,
  showDatePickerPricing,
  startDate,
  toolbarLabels,
  tripType,
  usePoints,
}: UseBestFaresPickerStateInput) => {
  const { isError, isLoading, monthlyMins, prices } = useBestFaresCalendar({
    destination,
    endDate,
    origin,
    passengers,
    showDatePickerPricing,
    startDate,
    tripType,
    usePoints,
  });
  const { currencyCode, inbound, outbound } = prices;
  const { inbound: inboundMonthlyMins, outbound: outboundMonthlyMins } =
    monthlyMins;

  const faresByDate = fareDirection === "inbound" ? inbound : outbound;
  const monthlyMinsByDate =
    fareDirection === "inbound" ? inboundMonthlyMins : outboundMonthlyMins;

  const renderDate = useBestFaresRenderDate({
    currencyCode,
    faresByDate,
    isLoading,
    monthlyMinsByDate,
    usePoints,
  });

  const isRequestEnabled = Boolean(
    showDatePickerPricing && origin && destination,
  );
  const message = getToolbarMessage({
    focusState,
    isError,
    isLoading,
    isRequestEnabled,
    labels: toolbarLabels,
    selectedDepartDate,
    selectedReturnDate,
    tripType,
    usePoints,
  });
  const selectedPrice = getSelectedToolbarPrice({
    isError,
    isRequestEnabled,
    prices,
    selectedDepartDate,
    selectedReturnDate,
    tripType,
    usePoints,
  });
  const announcement = getToolbarAnnouncement({
    isError,
    isLoading,
    isRequestEnabled,
    selectedPrice,
  });

  return {
    announcement,
    message,
    renderDate,
    selectedPrice,
  };
};
